import React, { useState } from "react";
import { Paragraph } from "components/Text";
import Pagination from "components/Pagination";
import { Button } from "components/Button/Button.styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import DropdownButton from "components/Dropdown/DropdownButton";
import styled from "styled-components";
import { ITableProps } from "./Table.interface";

const Table: React.FC<ITableProps> = ({ data = [], columns = [], children }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filter] = useState("");
  const [visibleColumns, setVisibleColumns] = useState(columns);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc" | "normal";
  }>({ key: columns[0], direction: "normal" });

  const filteredData = data?.filter((row) =>
    columns.some((column) =>
      row[column].toString().toLowerCase().includes(filter.toLowerCase())
    )
  );

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" | "normal" = "asc";
    if (sortConfig && sortConfig.key === key) {
      if (sortConfig.direction === "asc") {
        direction = "desc";
      } else if (sortConfig.direction === "desc") {
        direction = "normal";
      }
    }
    setSortConfig({ key, direction });
  };

  const sortData = (a: any, b: any, key: string, direction: "asc" | "desc") => {
    const order = direction === "asc" ? 1 : -1;

    const aValue = a[key];
    const bValue = b[key];

    // Handle cases where values might be undefined
    if (aValue === undefined || bValue === undefined) {
      return 0;
    }

    // If both values are numbers, sort numerically
    if (typeof aValue === "number" && typeof bValue === "number") {
      return (aValue - bValue) * order;
    }

    // If both values are strings, try to sort by number if possible, else sort alphabetically
    if (typeof aValue === "string" && typeof bValue === "string") {
      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);

      // If both values are valid numbers, sort numerically
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return (aNum - bNum) * order;
      }
      // If not, sort alphabetically
      return aValue.localeCompare(bValue) * order;
    }

    // Handle cases where types are mixed (e.g., string vs. number)
    return aValue.toString().localeCompare(bValue.toString()) * order;
  };

  const sortedData = [...filteredData].sort((a, b) => {
    if (sortConfig !== null && sortConfig.direction !== "normal") {
      return sortData(a, b, sortConfig.key, sortConfig.direction);
    }
    return 0;
  });

  const currentRows = sortedData?.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const Icon = styled.i`
    margin-left: auto;
    margin-right: 2rem;
    color: ${(props) =>
      props.color || props.theme.theme.TEXT.PRIMARY} !important;
  `;

  const getSortIcon = (key: string) => {
    if (sortConfig && sortConfig.key === key) {
      if (sortConfig.direction === "asc") {
        return <Icon className="cl cl-m-arrow-up ml-auto mr-2" />;
      } else if (sortConfig.direction === "desc") {
        return <Icon className="cl cl-m-arrow-down ml-auto mr-2" />;
      }
    }
    return null;
  };

  const handlePrint = () => {
    const printContent = document.getElementById("table-to-print")?.innerHTML;
    if (printContent) {
      const printWindow = window.open("", "", "width=800,height=600");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Print Table</title>
              <style>
                body {
                  font-family: Arial, sans-serif;
                }
                table {
                  width: 100%;
                  border-collapse: collapse;
                }
                th, td {
                  border: 1px solid black;
                  padding: 8px;
                  text-align: left;
                }
                th {
                  background-color: #f2f2f2;
                }
              </style>
            </head>
            <body>
              ${printContent}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }
  };

  const toggleColumnVisibility = (column: string) => {
    setVisibleColumns((prev) => {
      if (prev.includes(column)) {
        return prev.filter((col) => col !== column);
      } else {
        return columns.filter((col) => prev.includes(col) || col === column);
      }
    });
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  return (
    <div style={{ maxWidth: "100%", overflowX: "auto", position: "relative" }}>
      <div
        className="mb-3 align-items-center"
        style={{
          position: "sticky",
          left: 0,
          display: "flex",
          justifyContent: "space-between",
          padding: "8px 0",
          zIndex: 1000,
        }}
      >
        <div className="d-flex align-items-center">
          <Button
            className="btn btn-primary"
            onClick={handlePrint}
            data-testid="print-button"
          >
            <FontAwesomeIcon icon={faPrint} />
          </Button>
          <DropdownButton
            columns={columns}
            visibleColumns={visibleColumns}
            toggleColumnVisibility={toggleColumnVisibility}
            aria-label="column-visibility-button"
            data-testid="column-visibility-button"
          />
        </div>
        <div className="d-flex">
          <Paragraph>
            <label htmlFor="slctRecordsPerPage">Registros por página:</label>
          </Paragraph>
          <select
            className="form-control ml-2"
            id="slctRecordsPerPage"
            aria-label="Registros por página"
            data-testid="slctRecordsPerPage"
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
          >
            {[10, 20, 50, 100].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div id="table-to-print">
        <table
          className="table table-striped table-bordered"
          style={{
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column}
                  onClick={() => handleSort(column)}
                  aria-label={`column-${column}`}
                  aria-sort={
                    sortConfig.key === column
                      ? sortConfig.direction === "asc"
                        ? "ascending"
                        : sortConfig.direction === "desc"
                          ? "descending"
                          : "none"
                      : undefined
                  }
                  aria-testid="table-column-header"
                >
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <Paragraph style={{ cursor: "pointer" }}>
                      <strong>{column}</strong>
                    </Paragraph>
                    {getSortIcon(column)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows && currentRows.length > 0 ? (
              currentRows.map((row) => (
                <tr role="row" key={`row_${JSON.stringify(row)}`}>
                  {visibleColumns.map((column) => (
                    <td key={column}>
                      <Paragraph>{row[column]}</Paragraph>
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr role="row" key="empty">
                <td colSpan={visibleColumns.length}>
                  <Paragraph>No se encontraron registros.</Paragraph>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {children && <div className="mt-3">{children}</div>}
      <div
        className="pagination-container text-center"
        style={{
          position: "sticky",
          left: 0,
          zIndex: 1000,
          width: "100%",
          padding: "8px 0",
          borderTop: "1px solid #ddd",
        }}
      >
        <Pagination
          pagination={{
            page: currentPage,
            total: filteredData?.length,
            limit: rowsPerPage,
          }}
          setData={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default Table;
